import React, { PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { CBox } from '@/src/components/c-box/CBox';
import { CFlex, AlignItems, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CButton, CButtonPattern, CButtonShape, CButtonSize } from '@/src/components/c-button/CButton';
import { CText, CTextSize, CTextWeight } from '@/src/components/c-text/CText';
import { CDivider, CDividerColor } from '@/src/components/c-divider/CDivider';
import { CIcons } from '@/src/components/c-icon/CIcon';
import { CSpacing, NativeProps } from '@/src/components/interfaces';
import { PresenterStore } from '@/src/infrastructure/presenter/store';
import { useCPortal } from '@/src/infrastructure/provider';
import { CPortalLayer } from '@/src/infrastructure/provider/portals';

export enum CSidePanelSide {
  Left = 'left',
  Right = 'right',
}

export enum CSidePanelSize {
  Sm = 'sm',
  Md = 'md',
  Lg = 'lg',
}

export interface CSidePanelProps<TInput = void, TOutput = void> extends NativeProps {
  presenter: PresenterStore<TInput, TOutput>;
  side?: CSidePanelSide;
  size?: CSidePanelSize;
  curtain?: boolean;
  header: {
    title: string;
    slot?: React.ReactNode;
  };
  footer?: {
    slot?: React.ReactNode;
  };
}

export function CSidePanel<TInput = void, TOutput = void>(props: PropsWithChildren<CSidePanelProps<TInput, TOutput>>): React.JSX.Element | null {
  const portalContainer = useCPortal(CPortalLayer.SidePanel);
  const side = props.side ?? CSidePanelSide.Right;
  const size = props.size ?? CSidePanelSize.Md;
  const curtain = props.curtain ?? true;

  const { rendered, visible } = props.presenter.state;

  if (!rendered) return null;

  const panel = (
    <div
      className={classNames('c-side-panel__curtain', {
        'c-side-panel__curtain--visible': visible,
        'c-side-panel__curtain--transparent': !curtain,
      })}
      onClick={(event) => { event.stopPropagation(); props.presenter.reject(); }}
    >
      <aside
        className={classNames(
          'c-side-panel',
          `c-side-panel--side-${side}`,
          `c-side-panel--size-${size}`,
          {
            'c-side-panel--visible': visible,
            'c-side-panel--no-curtain': !curtain,
          },
          props.class,
        )}
        id={props.id}
        data-test-selector={props.testSelector ?? 'c-side-panel'}
        onClick={(event) => event.stopPropagation()}
        aria-modal="true"
        role="dialog"
      >
        <div className="c-side-panel__grid">
          <CBox class="c-side-panel__header c-padding--horizontal--xlg c-padding--vertical--md">
            <CFlex justifyContent={JustifyContent.SpaceBetween} alignItems={AlignItems.Center} fullHeight>
              <CFlex alignItems={AlignItems.Center} gap={CSpacing.Md}>
                {props.header.title && (
                  <CText size={CTextSize.Md} weight={CTextWeight.SemiBold}>{props.header.title}</CText>
                )}
                {props.header.slot}
              </CFlex>
              <CButton
                icon={CIcons.X}
                size={CButtonSize.Sm}
                pattern={CButtonPattern.Secondary}
                shape={CButtonShape.Square}
                onClick={(event: any) => { event.stopPropagation(); props.presenter.reject(); }}
              />
            </CFlex>
          </CBox>

          <CDivider color={CDividerColor.Light} marginVertical={CSpacing.None} marginHorizontal={CSpacing.Xlg} />

          <CBox class="c-side-panel__content c-padding--xlg overflow--x-hidden overflow--y-auto">
            {props.children}
          </CBox>

          {props.footer?.slot && (
            <>
              <CDivider color={CDividerColor.Light} marginVertical={CSpacing.None} marginHorizontal={CSpacing.Xlg} />
              <CBox class="c-side-panel__footer c-padding--horizontal--xlg c-padding--vertical--md">
                <CFlex alignItems={AlignItems.Center} justifyContent={JustifyContent.FlexEnd} fullHeight>
                  {props.footer.slot}
                </CFlex>
              </CBox>
            </>
          )}
        </div>
      </aside>
    </div>
  );

  return createPortal(panel, portalContainer ?? document.body);
}
